import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set worker source using jsdelivr CDN - this works and has all versions
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PdfViewer = ({ pdfUrl, onLoadError }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const containerRef = React.useRef(null);

  // Auto-fit width on mobile
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth - 32; // 16px padding each side
        setContainerWidth(width);
        // On mobile, start with fit-to-width scale
        if (window.innerWidth < 640 && width > 0) {
          // A4 is 595 points wide, so calculate scale to fit
          const fitScale = width / 595;
          setScale(Math.min(fitScale, 1.0));
        }
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (err) => {
    console.error('PDF load error:', err);
    setError('Kon PDF niet laden');
    setLoading(false);
    if (onLoadError) onLoadError(err);
  };

  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.25));

  if (!pdfUrl) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Geen PDF om weer te geven
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full max-w-full overflow-hidden">
      {/* Controls */}
      <div className="flex items-center justify-between p-2 border-b bg-muted/30 flex-wrap gap-2">
        <div className="flex items-center gap-1 sm:gap-2">
          <Button variant="outline" size="sm" onClick={goToPrevPage} disabled={pageNumber <= 1} className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs sm:text-sm whitespace-nowrap">
            {pageNumber} / {numPages || '...'}
          </span>
          <Button variant="outline" size="sm" onClick={goToNextPage} disabled={pageNumber >= (numPages || 1)} className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button variant="outline" size="sm" onClick={zoomOut} disabled={scale <= 0.25} className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3">
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs sm:text-sm w-10 text-center">{Math.round(scale * 100)}%</span>
          <Button variant="outline" size="sm" onClick={zoomIn} disabled={scale >= 2.5} className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3">
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* PDF Content */}
      <div ref={containerRef} className="flex-1 overflow-auto flex justify-center p-2 sm:p-4 bg-muted/20">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}
        
        {error && (
          <div className="flex items-center justify-center h-full text-destructive">
            {error}
          </div>
        )}

        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={null}
          className={loading || error ? 'hidden' : 'max-w-full'}
        >
          <Page 
            pageNumber={pageNumber} 
            scale={scale}
            width={containerWidth && window.innerWidth < 640 ? containerWidth : undefined}
            renderTextLayer={true}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>
    </div>
  );
};

export default PdfViewer;
